import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { getArticleStatusStyle } from '../../../../utils/articlesHelpers';
import { ReactComponent as EyeOpen } from '../../../../assets/images/eyeopen.svg';
import { ReactComponent as EyeClose } from '../../../../assets/images/eyeclose.svg';
import HighlightText from '../HighlightText/HighlightText';

function VisibilityChip({ visibility }) {
    const isPublic = (visibility || 'Public').toLowerCase() === 'public';
    return (
        <Box sx={{
            width: isPublic ? '80px' : '88px',
            height: '24px',
            borderRadius: '5px',
            border: '1px solid #D5DCE1',
            background: isPublic ? '#FFF' : '#F9FAFA',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            color: '#405261',
            fontFamily: 'Inter',
            fontSize: '12px',
            fontWeight: 500,
        }}>
            {isPublic ? <EyeOpen /> : <EyeClose />}
            {isPublic ? 'Public' : 'Internal'}
        </Box>
    );
}

const headerSx = {
    fontWeight: 400,
    fontSize: '12px',
    color: '#405261',
    fontFamily: 'Inter',
    paddingBottom: '0px',
    paddingLeft: '0px',
    borderBottom: '1px solid #E5E9EC',
};

const cellSx = {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 400,
    color: '#405261',
    borderBottom: '1px solid #E5E9EC',
    py: 0,
    paddingLeft: '0px',
    verticalAlign: 'middle',
};

import { useNavigate } from 'react-router-dom';

const KnowledgeBaseTable = ({ articles, searchQuery = '' }) => {
    const navigate = useNavigate();
    if (!articles || articles.length === 0) return null;

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleDateString();
    };

    return (
        <Box sx={{ marginBottom: '32px' }}>
            <Typography sx={{ color: '#061522', fontFamily: 'Inter', fontSize: '18px', fontWeight: 500, lineHeight: '18px', marginBottom: '5px' }}>
                Knowledge Base ({articles.length})
            </Typography>

            <TableContainer sx={{ boxShadow: 'none' }}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headerSx, width: '40%' }}>Title</TableCell>
                            <TableCell sx={{ ...headerSx, width: '10%' }}>Status</TableCell>
                            <TableCell sx={{ ...headerSx, width: '15%' }}>Created by</TableCell>
                            <TableCell sx={{ ...headerSx, width: '13%' }}>Category</TableCell>
                            <TableCell sx={{ ...headerSx, width: '12%' }}>Visibility</TableCell>
                            <TableCell sx={{ ...headerSx, width: '10%' }}>Last Updated</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {articles.map((article) => {
                            const createdBy = article.authorId?.full_name || 'System';
                            const category = article.categoryIds?.[0]?.name || 'General';
                            const visibility = article.visibility || 'Public';

                            return (
                                <TableRow
                                    key={article._id}
                                    hover
                                    onClick={() => navigate(`/kb/article/${article._id}`)}
                                    sx={{
                                        cursor: 'pointer',
                                        '& td': { height: '4rem', py: 0, verticalAlign: 'middle' },
                                    }}
                                >
                                    <TableCell sx={cellSx}>
                                        <HighlightText
                                            text={article.title}
                                            query={searchQuery}
                                            sx={{
                                                color: '#061522',
                                                fontSize: 14,
                                                fontFamily: 'Inter',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: '300px',
                                                display: 'block'
                                            }}
                                        />
                                    </TableCell>

                                    <TableCell sx={cellSx}>
                                        <Box sx={getArticleStatusStyle(article.status)}>
                                            {article.status?.toLowerCase() === 'pendingreview' ? 'Pending Review' : article.status}
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={cellSx}>
                                        <HighlightText
                                            text={createdBy}
                                            query={searchQuery}
                                            sx={{ color: '#061522', fontSize: 14, fontFamily: 'Inter' }}
                                        />
                                    </TableCell>

                                    <TableCell sx={cellSx}>
                                        <HighlightText
                                            text={category}
                                            query={searchQuery}
                                            sx={{ color: '#405261', fontSize: 14, fontFamily: 'Inter', }}
                                        />
                                    </TableCell>

                                    <TableCell sx={cellSx}>
                                        <VisibilityChip visibility={visibility} />
                                    </TableCell>

                                    <TableCell sx={cellSx}>
                                        {formatTimestamp(article.updatedAt)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default KnowledgeBaseTable;
